import React from "react";
import { useSubscriptionAccess, type SubscriptionFeature } from "@/hooks/useSubscriptionAccess";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";

interface FeatureGateProps {
  feature: SubscriptionFeature;
  children: React.ReactNode;
  title?: string;
}

const FEATURE_LABELS: Record<SubscriptionFeature, string> = {
  thesiscribe: "ThesiScribe",
  organizer: "Organizer",
  dictation: "Dictation Lab",
  budgeting: "Budgeting Studio",
  virtual: "Virtual Room",
};

export const FeatureGate: React.FC<FeatureGateProps> = ({ feature, children, title }) => {
  const { access, loading, error, refetch } = useSubscriptionAccess();
  const featureName = title || FEATURE_LABELS[feature] || "This feature";

  if (loading) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center text-center space-y-4">
        <Loader2 className="w-6 h-6 text-white animate-spin" />
        <p className="text-white/70">Checking your {featureName} subscription…</p>
      </div>
    );
  }

  if (error) {
    // If there's an error checking subscription, allow access by default
    // (user can still use the app even if subscription API fails)
    console.warn('[FeatureGate] Subscription check failed, allowing access:', error);
    return <>{children}</>;
  }

  const enabled = access[feature];
  if (enabled) {
    return <>{children}</>;
  }


  return (
    <div className="min-h-[70vh] flex flex-col items-center justify-center text-center space-y-4 px-6">
      <div className="space-y-2">
        <p className="text-sm uppercase tracking-[0.4em] text-white/40">Subscription locked</p>
        <h1 className="text-3xl font-semibold text-white">Unlock {featureName}</h1>
        <p className="text-white/70 max-w-lg">
          {featureName} lives under your Pronoia Wellness plan. Add it to your subscription from the BBQ Community portal to
          keep drafting, organizing, or dictating without interruption.
        </p>
      </div>
      <div className="flex flex-col sm:flex-row gap-3">
        <Button asChild>
          <a href="https://community.blackbetterment.quest/subscriptions" target="_blank" rel="noreferrer">
            Manage subscription
          </a>
        </Button>
        <Button variant="outline" asChild>
          <a href="/" className="text-white/80">
            Go back home
          </a>
        </Button>
      </div>
    </div>
  );
};
